import { type Coordinates } from "$lib/features/_other/coordinates.type";
import type { RouteSegment } from "$lib/features/route/routeSegment.type";
import { ROAD_SURFACE_COLORS, RoadSurface } from "$lib/features/route/routeStep/roadSurface.enum";

export const MapTrue = 1 as const;
export const MapFalse = 0 as const;
export type MapBoolean = typeof MapTrue | typeof MapFalse;

export function metersToFormattedDistance(meters: number): string {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)}km`;
    }
    return `${meters}m`;
}

/**
 * Formats a distance in meters as a short, human-readable string. Under 1 km it uses meters ("450 m"), otherwise kilometers with up to one decimal ("2.3 km").
 */
export const formatDistanceFromMeters = (meters: number): string => {
    const roundedMeters = Math.round(meters);
    return roundedMeters < 1000
        ? `${roundedMeters} m`
        : `${Number((roundedMeters / 1000).toFixed(1))} km`;
};

export function metersToMapPixels(meters: number, latitude: number, zoom: number): number {
    const earthCircumference = 40075016.686;
    const latRad = (latitude * Math.PI) / 180;
    const metersPerPixel = (earthCircumference * Math.cos(latRad)) / (512 * Math.pow(2, zoom));
    return meters / metersPerPixel;
}

// Ensure that if the map is zoomed out such that multiple copies of the feature are visible, the popup appears over the copy being pointed to.
export function snapToNearestMapCopy(coords: Coordinates, mapCoords: Coordinates): Coordinates {
    const updatedCoords = { ...coords };
    while (Math.abs(mapCoords.longitude - updatedCoords.longitude) > 180) {
        updatedCoords.longitude += mapCoords.longitude > updatedCoords.longitude ? 360 : -360;
    }
    return updatedCoords;
}

export function calculateElevationProfile(segments: RouteSegment[]): ElevationProfile | null {
    interface Point {
        elevation: number;
        distance: number;
    }

    const rawPoints: Point[] = [];
    let maxElev = -Infinity;
    let minElev = Infinity;
    let totalDistance = 0;

    for (const segment of segments) {
        for (const step of segment.steps) {
            if (step.elevationMeters > maxElev) maxElev = step.elevationMeters;
            if (step.elevationMeters < minElev) minElev = step.elevationMeters;

            rawPoints.push({
                elevation: step.elevationMeters,
                distance: totalDistance
            });

            totalDistance += step.distanceMeters;
        }
    }

    if (rawPoints.length < 2 || totalDistance <= 0) return null;

    rawPoints.push({
        elevation: rawPoints[rawPoints.length - 1].elevation,
        distance: totalDistance
    });

    // Smooth staircase/glitches with a 3-point moving average
    const points: Point[] = rawPoints.map((p, i) => {
        const start = Math.max(0, i - 1);
        const end = Math.min(rawPoints.length, i + 2);
        const slice = rawPoints.slice(start, end);
        const avgElev = slice.reduce((sum, item) => sum + item.elevation, 0) / slice.length;
        return { distance: p.distance, elevation: avgElev };
    });

    // Calculate true gain with a 2m noise filter
    let cumulativeElevationGain = 0;
    const ELEVATION_THRESHOLD = 2.0;

    let lastElev = points[0].elevation;
    for (let i = 1; i < points.length; i++) {
        const diff = points[i].elevation - lastElev;
        if (diff >= ELEVATION_THRESHOLD) {
            cumulativeElevationGain += diff;
            lastElev = points[i].elevation;
        } else if (diff <= -ELEVATION_THRESHOLD) {
            lastElev = points[i].elevation;
        }
    }

    // Prevent flat routes from stretching across the full height
    // If the real difference is < 40m, pad the range so a 9m walk looks like a gentle ripple
    const actualRange = maxElev - minElev;
    const MIN_VISUAL_RANGE = 40; // minimum meters to scale against
    const visualRange = Math.max(actualRange, MIN_VISUAL_RANGE);
    const midElev = (maxElev + minElev) / 2;
    const visualMin = midElev - visualRange / 2;

    const svgPoints = points
        .map((p) => {
            const x = (p.distance / totalDistance) * 100;
            const y = 100 - ((p.elevation - visualMin) / visualRange) * 100;
            return `${x.toFixed(1)},${Math.min(100, Math.max(0, y)).toFixed(1)}`;
        })
        .join(' ');

    let runningDist = 0;
    const stops: { x: number; index: number }[] = [];
    stops.push({ x: 0, index: 1 });
    for (let i = 0; i < segments.length; i++) {
        runningDist += segments[i].distanceMeters;
        const x = Math.min(100, Number(((runningDist / totalDistance) * 100).toFixed(1)));
        stops.push({ x, index: i + 2 });
    }

    return {
        min: Math.round(minElev),
        max: Math.round(maxElev),
        gain: Math.round(cumulativeElevationGain),
        svgPoints,
        stops
    };
}

export function calculateSurfaceStats(segments: RouteSegment[]): SurfaceStat[] {
    const stats: Partial<Record<RoadSurface, number>> = {};
    let totalDistance = 0;

    segments.forEach((segment) => {
        segment.steps.forEach((step) => {
            // Safely fallback using the Enum
            const surface = step.roadSurface || RoadSurface.UNKNOWN;
            stats[surface] = (stats[surface] || 0) + step.distanceMeters;
            totalDistance += step.distanceMeters;
        });
    });

    if (totalDistance === 0) return [];

    return (Object.entries(stats) as [RoadSurface, number][])
        .map(([surface, distance]) => ({
            surface,
            percentage: (distance / totalDistance) * 100,
            color: ROAD_SURFACE_COLORS[surface]
        }))
        .filter((stat) => stat.percentage > 0)
        .sort((a, b) => b.percentage - a.percentage);
}

export function getStreetViewUrl(coords: Coordinates): string {
    return `https://maps.google.com/maps?q=&layer=c&cbll=${coords.latitude},${coords.longitude}`;
}

export interface ElevationProfile {
    min: number;
    max: number;
    gain: number;
    svgPoints: string;
    stops: { x: number; index: number }[];
}

export interface SurfaceStat {
    surface: RoadSurface;
    percentage: number;
    color: string;
}